/**
 * 门店营收单组件独立访问页（独立页面框架，无侧栏/顶栏，适配手机端）
 * 通过 URL 参数动态加载指定门店的指定组件，与截图内容一致。
 * 用于从 Genie 等外部点击截图后跳转：白底、无嵌套，仅展示目标组件。
 *
 * 访问示例：/store-revenue-monitor/component?hotelId=7&componentType=customer-source
 * 若 URL 未带 storeName，会根据 hotelId 请求酒店名称并显示。
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Empty, Spin, Alert, message } from "antd";
import { ReloadOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { getHotelList } from "../../service/hotelApi";
import { aiConfigApi } from "../../service/aiConfigApi";
import { buildComponentAnalysisPrompt } from "../../service/aiPromptApi";
import { getComponentLabel, getComponentTimeDirection } from "./hooks/useAiAssistant";
import { storeComponentUsesTimeDirection } from "./utils/storeComponentBundle";
import { buildComponentDataPackage } from "./utils/buildComponentDataPackage";
import type { StoreData } from "./hooks/useLoadingStoreData";
import { StoreDataLoader } from "@ms/share";

/** 可从 URL 动态加载的有效组件类型，与 StoreDataLoader 中渲染的组件一一对应 */
const VALID_COMPONENT_TYPES = new Set([
  "store-overview",
  "expected-arrival-today",
  "expected-departure-today",
  "in-house-today",
  "arrival-today",
  "checkout-today",
  "inhouse-today",
  "room-status",
  "hourly-price-by-room",
  "room-status-recently",
  "channel-price",
  "order-traffic",
  "revpar",
  "room-health",
  "customer-source",
  "discount-analysis",
  "channel-analysis",
  "channel-info",
  "occupancy-analysis",
  "operation-efficiency",
  "price-alert",
]);

const COMPACT_SCREENSHOT_COMPONENTS = new Set([
  "expected-arrival-today",
  "expected-departure-today",
  "in-house-today",
  "arrival-today",
  "checkout-today",
  "inhouse-today",
]);

/** 与看板三列卡片接近的槽位宽度：组件在槽位内 100% 填充后再截图 */
const SCREENSHOT_COMPONENT_SLOT_WIDTH = 392;

/** 独立组件页：通过 URL 参数 hotelId、componentType 等加载指定门店的指定组件，供 Genie 等外部跳转使用 */
const StoreRevenueComponentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hotelIdRaw = searchParams.get("hotelId");
  const componentTypeRaw = searchParams.get("componentType") || "";
  const storeNameFromUrl = searchParams.get("storeName");
  const dateRangeFromUrl = searchParams.get("dateRange") || undefined;
  const orderTrafficGranularityFromUrl = searchParams.get("orderTrafficGranularity") || undefined;
  const isScreenshotMode = searchParams.get("screenshot") === "1";
  const storeNameFallback = hotelIdRaw ? `酒店${hotelIdRaw}` : "";
  const storeName = storeNameFromUrl || storeNameFallback;

  /** 当 URL 未传 storeName 时，根据 hotelId 拉取真实酒店名称并显示 */
  const [resolvedStoreName, setResolvedStoreName] = useState<string | null>(
    storeNameFromUrl ? storeName : null,
  );

  const hotelId = hotelIdRaw ? Number(hotelIdRaw) : 0;
  const componentType = VALID_COMPONENT_TYPES.has(componentTypeRaw)
    ? componentTypeRaw
    : "room-status";

  useEffect(() => {
    if (hotelId > 0 && !storeNameFromUrl) {
      let cancelled = false;
      getHotelList({ id: hotelId, pageSize: 1 })
        .then((res) => {
          if (cancelled || !res?.data?.list?.length) return;
          const first = res.data.list[0];
          const name = first.name || first.fullName || storeNameFallback;
          if (!cancelled) setResolvedStoreName(name);
        })
        .catch(() => {
          if (!cancelled) setResolvedStoreName(storeNameFallback);
        });
      return () => {
        cancelled = true;
      };
    }
    setResolvedStoreName(storeNameFromUrl ?? null);
    return undefined;
  }, [hotelId, storeNameFromUrl, storeNameFallback]);

  const displayStoreName = resolvedStoreName ?? storeName;

  /** 由 StoreDataLoader onStoreDataReady 回调写入，供底部「AI 分析」按钮使用 */
  const storeDataRef = useRef<StoreData | null>(null);
  const refreshFnRef = useRef<(() => void) | null>(null);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const onStoreDataReady = useCallback((data: StoreData) => {
    storeDataRef.current = data;
  }, []);

  const onRefreshReady = useCallback((refresh: () => void) => {
    refreshFnRef.current = refresh;
  }, []);

  /** 调用 aiConfigApi.analyzeWithPrompt，将当前组件数据打包后发送给 AI 分析 */
  const handleAiAnalyze = useCallback(async () => {
    const storeData = storeDataRef.current;
    if (!storeData) {
      message.info("数据加载中，请稍候再试");
      return;
    }
    setAnalysisError(null);
    setAnalysisLoading(true);
    const componentName = getComponentLabel(componentType);
    const dataPackage = buildComponentDataPackage(String(hotelId), componentType, storeData);
    const timeDirection = storeComponentUsesTimeDirection(componentType)
      ? getComponentTimeDirection(componentType)
      : undefined;
    try {
      const built = await buildComponentAnalysisPrompt({
        serviceName: "msCentral",
        pageId: "store-revenue-monitor",
        componentType,
        scenario: "single-component-page",
        locale: "zh-CN",
        storeName: displayStoreName,
        componentLabel: componentName,
        ...(timeDirection ? { timeDirection } : {}),
        dataPackage,
        dateRange: dateRangeFromUrl,
      });
      const prompt = built?.data?.prompt;
      if (!prompt) {
        throw new Error(built?.message || built?.msg || "构建提示词失败");
      }
      const result = await aiConfigApi.analyzeWithPrompt(prompt);
      setAnalysisResult(result);
    } catch (e: any) {
      setAnalysisError(e?.message ?? "AI 分析失败");
    } finally {
      setAnalysisLoading(false);
    }
  }, [hotelId, componentType, displayStoreName, dateRangeFromUrl]);

  const isValid = hotelId > 0 && componentType;
  const useCompactScreenshotSlot = isScreenshotMode && COMPACT_SCREENSHOT_COMPONENTS.has(componentType);
  const slotFrameStyle: React.CSSProperties = useCompactScreenshotSlot
    ? {
        width: SCREENSHOT_COMPONENT_SLOT_WIDTH,
        minWidth: SCREENSHOT_COMPONENT_SLOT_WIDTH,
        maxWidth: SCREENSHOT_COMPONENT_SLOT_WIDTH,
        margin: "0 auto",
      }
    : { width: "100%" };

  if (!isValid) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <Empty
          description="请通过链接访问并携带 hotelId、componentType 参数"
        >
          <Button type="primary" onClick={() => navigate("/store-revenue-monitor")}>
            返回
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        background: "#fff",
        boxSizing: "border-box",
        padding: 24,
      }}
    >
      <div style={slotFrameStyle}>
        <StoreDataLoader
          storeId={hotelId}
          storeName={displayStoreName}
          refreshKey={0}
          focusComponentType={componentType}
          onTitleClick={() => {}}
          onRefresh={() => {}}
          onComponentTitleClick={() => {}}
          onStoreDataReady={onStoreDataReady}
          onRefreshReady={onRefreshReady}
          bareLayout
          screenshotDateRange={dateRangeFromUrl}
          screenshotOrderTrafficGranularity={orderTrafficGranularityFromUrl}
        />
      </div>
      <div style={{ marginTop: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            size="large"
            onClick={() => refreshFnRef.current?.()}
          >
            刷新
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            size="large"
            loading={analysisLoading}
            onClick={handleAiAnalyze}
          >
            AI 分析
          </Button>
        </div>
        {analysisLoading && (
          <div style={{ padding: "12px 0" }}>
            <Spin tip="正在分析…" />
          </div>
        )}
        {analysisError && (
          <Alert
            type="error"
            message="分析失败"
            description={analysisError}
            showIcon
            style={{ maxWidth: 560, width: "100%" }}
          />
        )}
        {analysisResult && !analysisLoading && (
          <div
            style={{
              maxWidth: 560,
              width: "100%",
              padding: 16,
              background: "#fafafa",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
              whiteSpace: "pre-wrap",
              textAlign: "left",
            }}
          >
            {analysisResult}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreRevenueComponentPage;
